import { Banknote, Clock, MapPin, TrendingUp } from "lucide-react";
import { localHref } from "../../lib/links";
import { careersMailto } from "../../data/careers";
import { ButtonLink } from "../ui/Button";

/** Level, type, location and pay as a row of small labelled facts. */
export function JobFacts({ position, dark = false, className = "" }) {
  const facts = [
    { icon: TrendingUp, label: "Level", value: position.level },
    { icon: Clock, label: "Type", value: position.type },
    { icon: MapPin, label: "Location", value: position.location },
    { icon: Banknote, label: "Pay", value: position.compensation },
  ].filter(({ value }) => value);
  return (
    <ul
      className={`flex flex-wrap gap-x-6 gap-y-2 text-[15px] font-bold ${dark ? "text-white/90" : "text-slate"} ${className}`}
    >
      {facts.map(({ icon: Icon, label, value }) => (
        <li key={label} className="flex items-center gap-2">
          <Icon
            size={17}
            aria-hidden="true"
            className={dark ? "text-yellow" : "text-blue-deep"}
          />
          <span className="sr-only">{label}:</span>
          {value}
        </li>
      ))}
    </ul>
  );
}

/** An open position on the Careers page: role, facts, summary, actions. */
export default function JobCard({ position }) {
  const { title, path, summary } = position;
  return (
    <article className="group relative overflow-hidden rounded-brand border border-line bg-white shadow-soft transition-shadow hover:shadow-lift">
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-0 w-1 bg-yellow"
      />
      <div className="flex flex-col gap-6 p-6 sm:p-8 lg:flex-row lg:items-center lg:justify-between lg:gap-10">
        <div className="min-w-0">
          <span className="tag text-blue-deep">Now hiring</span>
          <h3 className="font-display mt-1.5 text-[26px] text-ink uppercase md:text-[30px]">
            {path ? (
              <a
                href={localHref(path)}
                className="transition-colors hover:text-blue-deep"
              >
                {title}
              </a>
            ) : (
              title
            )}
          </h3>
          <JobFacts position={position} className="mt-3" />
          {summary && (
            <p className="mt-3 max-w-[620px] text-[15.5px] text-muted">
              {summary}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-3 lg:flex-none lg:flex-col">
          <ButtonLink
            href={
              path ? `${path}#apply` : careersMailto(`Application: ${title}`)
            }
            size="md"
            aria-label={`Apply for ${title}`}
          >
            Apply now
          </ButtonLink>
          {path && (
            <ButtonLink
              href={path}
              variant="outline"
              size="md"
              aria-label={`Read the full ${title} job description`}
            >
              Read the full role
            </ButtonLink>
          )}
        </div>
      </div>
    </article>
  );
}
